/**
 * The picture panel: swap a slide's picture, or add one, from the picture bank.
 *
 * A drawer at the side of the editor rather than a modal, so the slide stays
 * in view and a teacher can try one picture, look, and try the next - each
 * swap is one undo step, and the panel stays open until they close it.
 *
 * Three sources, one grid:
 *   Bank    reviewed, openly licensed diagrams filed by spec topic
 *           (routes/imageBank.js). Opens on this deck's ticked topics.
 *   Photos  Unsplash, several at a time.
 *   Web     a live image search, for when neither has it.
 *
 * It knows nothing about slides. app.js opens it with the deck and an
 * onPick(choice) callback, and does the swapping itself:
 *   choice = { url, alt, kind, bankId?, unsplash?: { id, name, profileUrl, downloadLocation } }
 * A choice is only handed over once its full image has loaded, so a link that
 * refuses to be shown never lands on a slide as a broken icon.
 */
(function () {
    'use strict';

    let api = (path, opts) => fetch(path, { headers: { 'Content-Type': 'application/json' }, ...(opts || {}) });
    let root = null;
    let els = {};
    let opened = null;       // { presentationId, onPick, currentUrl, hint }
    let ctx = null;          // /context answer for this deck
    const view = { tab: 'bank', scope: 'deck', kind: '', q: '', offset: 0 };
    const queryFor = { photos: '', web: '' };
    let loadSeq = 0;

    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    function build() {
        root = document.createElement('aside');
        root.className = 'ibp';
        root.hidden = true;
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-label', 'Pictures');
        root.innerHTML = `
            <div class="ibp-head">
                <h2 class="ibp-title">Swap picture</h2>
                <button type="button" class="ibp-close" aria-label="Close">&times;</button>
            </div>
            <div class="ibp-tabs" role="tablist">
                <button type="button" role="tab" data-tab="bank" aria-selected="true">Picture bank</button>
                <button type="button" role="tab" data-tab="photos" aria-selected="false">Photos</button>
                <button type="button" role="tab" data-tab="web" aria-selected="false">Web</button>
            </div>
            <div class="ibp-tools">
                <input type="search" class="ibp-q" placeholder="Search diagrams" aria-label="Search">
                <div class="ibp-filters">
                    <select class="ibp-scope" aria-label="Topic"></select>
                    <div class="ibp-kinds" role="group" aria-label="Kind">
                        <button type="button" data-kind="" class="is-on">All</button>
                        <button type="button" data-kind="diagram">Diagrams</button>
                        <button type="button" data-kind="photo">Photos</button>
                    </div>
                </div>
                <p class="ibp-note" hidden></p>
            </div>
            <div class="ibp-body">
                <div class="ibp-grid"></div>
                <p class="ibp-empty" hidden></p>
                <button type="button" class="btn btn-ghost btn-sm ibp-more" hidden>Show more</button>
            </div>`;
        document.body.appendChild(root);
        els = {
            title: root.querySelector('.ibp-title'),
            q: root.querySelector('.ibp-q'),
            scope: root.querySelector('.ibp-scope'),
            filters: root.querySelector('.ibp-filters'),
            kinds: root.querySelector('.ibp-kinds'),
            note: root.querySelector('.ibp-note'),
            grid: root.querySelector('.ibp-grid'),
            empty: root.querySelector('.ibp-empty'),
            more: root.querySelector('.ibp-more'),
        };

        root.querySelector('.ibp-close').addEventListener('click', close);
        root.querySelector('.ibp-tabs').addEventListener('click', (e) => {
            const tab = e.target.closest('[data-tab]');
            if (tab) setTab(tab.dataset.tab);
        });
        els.kinds.addEventListener('click', (e) => {
            const b = e.target.closest('[data-kind]');
            if (!b) return;
            view.kind = b.dataset.kind;
            els.kinds.querySelectorAll('button').forEach((x) => x.classList.toggle('is-on', x === b));
            reload();
        });
        els.scope.addEventListener('change', () => { view.scope = els.scope.value; reload(); });

        // The bank filters as you type; the other two are searches that cost
        // something, so they wait for Enter or a pause.
        let typing = null;
        els.q.addEventListener('input', () => {
            clearTimeout(typing);
            typing = setTimeout(() => {
                if (view.tab === 'bank') { view.q = els.q.value.trim(); reload(); }
                else if (view.tab === 'photos') { queryFor.photos = els.q.value.trim(); reload(); }
            }, view.tab === 'bank' ? 200 : 600);
        });
        els.q.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            clearTimeout(typing);
            if (view.tab === 'bank') view.q = els.q.value.trim();
            else queryFor[view.tab] = els.q.value.trim();
            reload();
        });
        els.more.addEventListener('click', () => load(true));
        els.grid.addEventListener('click', (e) => {
            const item = e.target.closest('.ibp-item');
            if (item && item._choice) pick(item, item._choice);
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && root && !root.hidden) close();
        });
    }

    function setTab(tab) {
        view.tab = tab;
        root.querySelectorAll('[data-tab]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.tab === tab)));
        els.filters.hidden = tab !== 'bank';
        els.q.placeholder = tab === 'bank' ? 'Search diagrams' : tab === 'photos' ? 'Search photos' : 'Search the web, then press Enter';
        if (tab === 'bank') els.q.value = view.q;
        else {
            // Start a search off from what the teacher is looking at, so the
            // first thing they see is already close.
            if (!queryFor[tab]) queryFor[tab] = view.q || scopeLabel() || opened?.hint || '';
            els.q.value = queryFor[tab];
        }
        els.note.hidden = tab !== 'web';
        els.note.textContent = tab === 'web'
            ? 'Web pictures belong to whoever made them. Fine for your own lessons; check before you share the deck.'
            : '';
        reload();
    }

    /** The topic the bank is filtered to, as words for a search, or ''. */
    function scopeLabel() {
        if (view.scope.startsWith('t:')) return view.scope.split('|').slice(2).join('|');
        return '';
    }

    function drawScope() {
        const opts = [];
        const ticked = new Set(ctx?.ticked || []);
        const subject = ctx?.subject || '';
        if (ticked.size) opts.push(`<option value="deck">This deck’s topics</option>`);
        opts.push(`<option value="all">${subject ? `All ${esc(subject)}` : 'Everything'}</option>`);
        // Topics grouped by domain, as the spec-topic picker shows them.
        const byDomain = new Map();
        for (const t of ctx?.topics || []) {
            const key = t.domain;
            if (!byDomain.has(key)) byDomain.set(key, []);
            byDomain.get(key).push(t);
        }
        for (const [domain, list] of byDomain) {
            opts.push(`<optgroup label="${esc(domain)}">${list.map((t) =>
                `<option value="${esc(`t:${t.board}|${t.topic}`)}">${esc(t.topic)}${ticked.has(t.topic) ? ' ★' : ''} (${t.count})</option>`).join('')}</optgroup>`);
        }
        els.scope.innerHTML = opts.join('');
        if (![...els.scope.options].some((o) => o.value === view.scope)) view.scope = ticked.size ? 'deck' : 'all';
        els.scope.value = view.scope;
    }

    function bankQuery(offset) {
        const qs = new URLSearchParams();
        if (ctx?.subject) qs.set('subject', ctx.subject);
        if (ctx?.level) qs.set('level', ctx.level);
        if (view.scope === 'deck') {
            if (ctx?.board) qs.set('board', ctx.board);
            (ctx?.ticked || []).forEach((t) => qs.append('topic', t));
        } else if (view.scope.startsWith('t:')) {
            const [board, ...rest] = view.scope.slice(2).split('|');
            qs.set('board', board);
            qs.set('topic', rest.join('|'));
        }
        if (view.q) qs.set('q', view.q);
        if (view.kind) qs.set('kind', view.kind);
        if (offset) qs.set('offset', String(offset));
        return qs;
    }

    function reload() { view.offset = 0; load(false); }

    async function load(append) {
        const seq = ++loadSeq;
        if (!append) { els.grid.innerHTML = ''; }
        els.more.hidden = true;
        els.empty.hidden = false;
        els.empty.textContent = 'Loading…';
        let items = [];
        let more = false;
        try {
            if (view.tab === 'bank') {
                const res = await api('/api/image-bank?' + bankQuery(append ? view.offset : 0));
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Could not load pictures');
                items = (data.images || []).map((p) => ({
                    thumb: p.url, caption: p.caption, credit: p.credit,
                    choice: { url: p.url, alt: p.caption, kind: p.kind, bankId: p.id },
                }));
                more = !!data.more;
                view.offset = (append ? view.offset : 0) + items.length;
            } else if (view.tab === 'photos') {
                const q = queryFor.photos;
                if (q.length >= 2) {
                    const res = await api('/api/image-bank/photos?q=' + encodeURIComponent(q));
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Could not search photos');
                    items = (data.photos || []).map((p) => ({
                        thumb: p.thumbUrl || p.url, caption: p.alt, credit: `Photo by ${p.name} on Unsplash`,
                        choice: { url: p.url, alt: p.alt || q, kind: 'photo', unsplash: { id: p.id, name: p.name, profileUrl: p.profileUrl, downloadLocation: p.downloadLocation } },
                    }));
                }
            } else {
                const q = queryFor.web;
                if (q.length >= 2) {
                    const res = await api('/api/image-bank/web?q=' + encodeURIComponent(q));
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Could not search the web');
                    items = (data.images || []).map((p) => ({
                        thumb: p.thumbUrl || p.url, caption: p.title, credit: p.domain,
                        choice: { url: p.url, alt: p.title || q, kind: 'diagram' },
                    }));
                }
            }
        } catch (err) {
            if (seq !== loadSeq) return;
            els.empty.textContent = err.message || 'Something went wrong.';
            return;
        }
        if (seq !== loadSeq) return;   // a newer search has started

        for (const it of items) els.grid.appendChild(card(it));
        els.more.hidden = !more;
        const none = !els.grid.children.length;
        els.empty.hidden = !none;
        if (none) els.empty.textContent = emptyText();
    }

    function emptyText() {
        if (view.tab === 'bank') {
            if (view.q) return `No bank pictures match “${view.q}”. Try Photos or Web.`;
            if (view.scope !== 'all' && ctx?.subject) return `Nothing filed under ${view.scope === 'deck' ? 'this deck’s topics' : 'that topic'} yet. Try All ${ctx.subject}.`;
            return 'The bank has nothing for this subject yet. Try Photos or Web.';
        }
        return queryFor[view.tab].length < 2 ? 'Type what you are looking for.' : 'Nothing found. Try other words.';
    }

    function card(it) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ibp-item';
        if (opened?.currentUrl && it.choice.url === opened.currentUrl) b.classList.add('is-current');
        b.title = [it.caption, it.credit].filter(Boolean).join('\n');
        b.innerHTML = `<span class="ibp-thumb"><img src="${esc(it.thumb)}" alt="" loading="lazy" referrerpolicy="no-referrer"></span>
            <span class="ibp-cap">${esc(it.caption || '')}</span>`;
        // A web thumbnail that fails to load is a result nobody can judge.
        b.querySelector('img').addEventListener('error', () => { if (view.tab === 'web') b.remove(); }, { once: true });
        b._choice = it.choice;
        return b;
    }

    function pick(item, choice) {
        if (item.classList.contains('is-loading')) return;
        item.classList.add('is-loading');
        const probe = new Image();
        probe.referrerPolicy = 'no-referrer';
        probe.onload = () => {
            item.classList.remove('is-loading');
            els.grid.querySelectorAll('.is-current').forEach((x) => x.classList.remove('is-current'));
            item.classList.add('is-current');
            if (opened) opened.currentUrl = choice.url;
            opened?.onPick({ ...choice, width: probe.naturalWidth, height: probe.naturalHeight });
        };
        probe.onerror = () => {
            item.classList.remove('is-loading');
            item.classList.add('is-broken');
            item.title = 'This picture would not load. Try another.';
        };
        probe.src = choice.url;
    }

    /**
     * open({ presentationId, onPick, currentUrl, hint, mode })
     *   mode 'swap' (a picture is selected) or 'add' (nothing is)
     *   hint  words to start a photo or web search from, e.g. the picture's alt
     */
    async function open(options) {
        if (!root) build();
        const sameDeck = opened && ctx && opened.presentationId === options.presentationId;
        opened = { ...options };
        els.title.textContent = options.mode === 'add' ? 'Add a picture' : 'Swap picture';
        root.hidden = false;
        // The page makes room on a wide screen, so the slide is never under
        // the panel (the stage rescales itself to the space it is given).
        document.documentElement.classList.add('ibp-open');
        if (!sameDeck) {
            ctx = null;
            view.scope = 'deck';
            view.q = '';
            queryFor.photos = '';
            queryFor.web = '';
            try {
                const res = await api('/api/image-bank/context?presentationId=' + encodeURIComponent(options.presentationId || ''));
                ctx = res.ok ? await res.json() : null;
            } catch { ctx = null; }
            drawScope();
            // A subject the bank has nothing for (no topic list yet, see
            // docs/spec-topics-missing.md) opens on photos rather than on an
            // empty grid. Within a deck, the tab the teacher last chose stays.
            view.tab = ctx && ctx.topics && ctx.topics.length ? 'bank' : 'photos';
        }
        setTab(view.tab);
        els.q.focus({ preventScroll: true });
    }

    /** Re-aim an open panel at another picture (or at none) without reloading it. */
    function retarget({ mode, currentUrl, hint }) {
        if (!opened || !root || root.hidden) return;
        opened.mode = mode;
        opened.currentUrl = currentUrl;
        opened.hint = hint;
        els.title.textContent = mode === 'add' ? 'Add a picture' : 'Swap picture';
        els.grid.querySelectorAll('.ibp-item').forEach((x) => x.classList.toggle('is-current', !!currentUrl && x._choice?.url === currentUrl));
    }

    function close() {
        if (root) root.hidden = true;
        document.documentElement.classList.remove('ibp-open');
        opened?.onClose?.();
        if (opened) opened.onPick = () => {};
    }

    const isOpen = () => !!root && !root.hidden;

    window.ImageBankPanel = {
        init(opts) { if (opts && opts.api) api = opts.api; },
        open,
        retarget,
        close,
        isOpen,
    };
}());
